import styled from 'styled-components'
import ViiluFullPageDialog from 'ui/components/ViiluFullPageDialog'
import { useIntl } from 'react-intl'
import Assignees from './Assignees'
import usePurchaseInvoice from '../usePurchaseInvoice'
import LoadingState from 'ui/components/LoadingState'
import { LoadingContainer } from '../../components'
import { FormProvider, useForm } from 'react-hook-form'
import { useZodValidationResolver } from 'util/hooks/useZodValidationResolver'
import type { FormInputs } from './validation'
import { basicInformationFormSchema } from './validation'
import Attachments from './Attachments'
import Form from './Form'
import Accounting from './Accounting'
import StatusChip from './StatusChip'
import { useEffect } from 'react'
import { useSelectedFinanceCompany } from 'ui/ManagerFinance/hooks/useSelectedFinanceCompany'
import { purchaseInvoiceStatusFromState } from 'ui/ManagerFinance/utils/messages'
import type { Vat } from 'ui/ManagerFinance/Accounting/Voucher/types'
import ConfirmButton from './ConfirmButton'
import { PurchaseInvoiceStatus } from 'ui/ManagerFinance/enum'

const Wrapper = styled.div`
	height: 100%;
	overflow: auto;
`

const Content = styled.div`
	flex: 1;
	display: flex;
	overflow: auto;
	min-height: 60vh;
`

const Left = styled.div`
	padding: 16px;
	border-right: 1px solid rgba(0, 0, 0, 0.2);
`

const Right = styled.div`
	padding: 16px;
	flex: 1;
`

const Bottom = styled.div`
	display: flex;
	margin-top: 1rem;
	min-height: 40vh;
`

type PurchaseInvoiceDialogProps = {
	vats: Vat[]
	purchaseInvoiceId?: number
	onHide?: () => void
	onHideURL?: string
}

const PurchaseInvoiceDialog = ({
	vats,
	purchaseInvoiceId,
	onHide,
	onHideURL,
}: PurchaseInvoiceDialogProps) => {
	const intl = useIntl()

	const { company } = useSelectedFinanceCompany()

	const {
		purchaseInvoice,
		loading,
		refresh: refreshPurchaseInvoice,
	} = usePurchaseInvoice({
		companyUUID: company?.uuid,
		databaseSource: company?.integrations?.viiluTalous?.databaseSource,
		purchaseInvoiceId,
	})

	const status = purchaseInvoiceStatusFromState(
		purchaseInvoice?.state,
		new Date(),
	)

	const resolver = useZodValidationResolver<FormInputs>(
		basicInformationFormSchema(intl),
	)

	const formMethods = useForm<FormInputs>({
		resolver,
		defaultValues: {
			...purchaseInvoice,
		},
		mode: 'onBlur',
		shouldUnregister: false,
	})

	const refresh = async () => {
		await refreshPurchaseInvoice()
	}

	useEffect(() => {
		formMethods.reset({ ...purchaseInvoice })
	}, [formMethods, purchaseInvoice])

	const renderContent = () => {
		if (loading) {
			return (
				<LoadingContainer>
					<LoadingState />
				</LoadingContainer>
			)
		}

		return (
			<Wrapper>
				<FormProvider {...formMethods}>
					<Content>
						<Left>
							<StatusChip
								status={purchaseInvoiceStatusFromState(
									purchaseInvoice?.state,
									purchaseInvoice?.dueDate,
								)}
							/>
							<Form />
							<Assignees />
						</Left>

						<Right>
							<Attachments attachments={purchaseInvoice?.attachments || []} />
						</Right>
					</Content>
					<Bottom>
						<Accounting vats={vats} />
					</Bottom>
				</FormProvider>
			</Wrapper>
		)
	}

	const actions: JSX.Element[] = []

	if (status === PurchaseInvoiceStatus.NEW) {
		actions.push(
			<ConfirmButton
				purchaseInvoiceId={purchaseInvoiceId}
				databaseSource={company?.integrations?.viiluTalous?.databaseSource}
				companyUUID={company?.uuid}
				refresh={refresh}
			/>,
		)
	}

	return (
		<ViiluFullPageDialog
			id="purchase-invoice-dialog"
			title={
				purchaseInvoiceId
					? intl.formatMessage({
							defaultMessage: 'Ostolasku',
							description: 'Title for purchase invoice dialog',
					  })
					: intl.formatMessage({
							defaultMessage: 'Ostolaskun luonti',
							description: 'Title of create purchase invoice',
					  })
			}
			visible
			onHide={onHide}
			onHideURL={onHideURL}
			focusOnMount={false}
			toolbarStyle={{
				background: 'var(--color-secondary-dark)',
			}}
			navActions={actions}
		>
			{renderContent()}
		</ViiluFullPageDialog>
	)
}

export default PurchaseInvoiceDialog
